import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { SearchBarProps } from '../types';

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 512 512'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <title>Search</title>
    <path
      d='M456.69 421.39L362.6 327.3a173.81 173.81 0 0034.84-104.58C397.44 126.38 319.06 48 222.72 48S48 126.38 48 222.72s78.38 174.72 174.72 174.72A173.81 173.81 0 00327.3 362.6l94.09 94.09a25 25 0 0035.3-35.3zM97.92 222.72a124.8 124.8 0 11124.8 124.8 124.95 124.95 0 01-124.8-124.8z'
      fill='currentColor'
      stroke='currentColor'
    />
  </svg>
);

const ClearIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width='20'
    height='20'
    viewBox='0 0 512 512'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className={className}
  >
    <title>Close Circle</title>
    <path
      d='M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm75.31 260.69a16 16 0 11-22.62 22.62L256 278.63l-52.69 52.68a16 16 0 01-22.62-22.62L233.37 256l-52.68-52.69a16 16 0 0122.62-22.62L256 233.37l52.69-52.68a16 16 0 0122.62 22.62L278.63 256z'
      fill='currentColor'
    />
  </svg>
);

const SearchBar: React.FC<SearchBarProps> = ({
  value = '',
  onChange,
  placeholder = 'Search...',
  debounceMs = 300,
}) => {
  const [searchValue, setSearchValue] = useState(value);
  const debouncedOnChange = useRef(
    debounce((value: string) => {
      onChange?.(value);
    }, debounceMs)
  );

  const handleInputChange = useCallback((newValue: string) => {
    setSearchValue(newValue);
    debouncedOnChange.current(newValue);
  }, []);

  const handleClear = useCallback(() => {
    setSearchValue('');
    onChange?.('');
  }, [onChange]);

  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  return (
    <div
      className={`flex items-center border border-neutral-light rounded-md bg-white h-8`}
    >
      <SearchIcon className='ml-2 w-4 h-4 text-neutral' />

      <input
        type='text'
        value={searchValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleInputChange(e.target.value)
        }
        placeholder={placeholder}
        className='flex-1 h-full border-0 border-none focus:ring-0 focus:outline-none shadow-none bg-transparent pl-0'
        aria-label='Search'
        data-testid='table-header__search-input'
      />

      <div className='flex items-center justify-center h-full mx-1'>
        <button
          type='button'
          onClick={handleClear}
          className='cursor-pointer flex justify-center items-center text-neutral-lighter hover:text-neutral-dark transition-colors'
          data-testid='table-header__search-input-close'
          aria-label='Clear search'
        >
          <ClearIcon className='w-5 h-5' />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
